import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TagIcon from '@mui/icons-material/Tag';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import RegistrationAction from '../components/RegistrationAction';

export { ParticipantRowActionsMenu } from './cells/ActionsMenuCell';
export {
  ParticipantExpandToggle,
  
  ParticipantNameWithDetailsCell,
  
} from './cells/NameCell';
export { ParticipantQrCell } from './cells/QrCell';
export {
  needsDateOfBirthWarning,
  PARTICIPANT_DETAIL_ROW_EXTRA_HEIGHT,
  PARTICIPANT_DOB_WARNING_ROW_EXTRA_HEIGHT,
  ParticipantPrimaryRowBand,
  
  type ParticipantCellCallbacks,
  
} from './cells/shared';

import {
  ParticipantCellCallbacks,
  ParticipantCellProps,
  useParticipantRowModel,
} from './cells/shared';

export function ParticipantCompetitionCell({ participant, callbacks }: ParticipantCellProps) {
  const { competitionName } = useParticipantRowModel(participant, callbacks);
  return competitionName;
}

export function ParticipantDistanceCell({ participant, callbacks }: ParticipantCellProps) {
  const { distanceName } = useParticipantRowModel(participant, callbacks);
  return distanceName;
}

export function ParticipantPriceCell({ participant, callbacks }: ParticipantCellProps) {
  const { price, t } = useParticipantRowModel(participant, callbacks);
  return price != null ? t('base.currencyValue', { count: price }) : '-';
}

export function ParticipantStatusCell({ participant, callbacks }: ParticipantCellProps) {
  const { t, registrationState, signupInfo } = useParticipantRowModel(participant, callbacks);
  const chip = (
    <Chip
      label={
        registrationState === 'unregistered'
          ? t('participants.noRegistration')
          : registrationState === 'checked_in'
            ? t('participants.checkedIn')
            : t('registrations.registered')
      }
      size="small"
      variant={registrationState === 'unregistered' ? 'outlined' : undefined}
      color={
        registrationState === 'checked_in'
          ? 'success'
          : registrationState === 'registered'
            ? 'info'
            : 'default'
      }
      icon={
        registrationState === 'checked_in' ? (
          <CheckCircleIcon sx={{ fontSize: 16 }} />
        ) : undefined
      }
      sx={{
        maxWidth: '100%',
        height: 24,
        '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', lineHeight: 1.2 },
        '& .MuiChip-icon': { ml: 0.5 },
      }}
    />
  );

  if (!signupInfo) {
    return chip;
  }

  return (
    <Tooltip title={signupInfo}>
      <span>{chip}</span>
    </Tooltip>
  );
}

export function ParticipantEntryCell({ participant, callbacks }: ParticipantCellProps) {
  const model = useParticipantRowModel(participant, callbacks);
  const entryMenu = usePopupState({ variant: 'popover', popupId: `entry-menu-${model.id}` });
  const { t } = model;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
      onClick={e => e.stopPropagation()}
    >
      {(model.entryLabel || model.registrationState === 'checked_in') && (
        <Typography
          variant="body2"
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}
        >
          {model.entryLabel ? <span>{model.entryLabel}</span> : null}
          {model.entryLabel && model.registrationState === 'checked_in' && <span aria-hidden> · </span>}
          {model.registrationState === 'checked_in' && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              <TagIcon sx={{ fontSize: 18 }} aria-hidden />
              {model.hasValidSerial ? model.serialNumber : '-'}
            </Box>
          )}
        </Typography>
      )}
      {callbacks.register &&
        !model.isRegistered &&
        canRegisterParticipant(participant) && (
        <Tooltip title={model.rowRegisterTooltip}>
          <IconButton
            size="small"
            color="primary"
            onClick={model.handlers.register}
            aria-label={t('registrations.register')}
            {...(participant.id === callbacks.tourRegisterAnchorParticipantId
              ? { 'data-tour': 'my-participants-register' }
              : {})}
          >
            <ConfirmationNumberIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {model.showEntryMenu && (
        <>
          <IconButton
            size="small"
            {...bindTrigger(entryMenu)}
            aria-label={t('participants.entryColumn')}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu {...bindMenu(entryMenu)}>
            {model.canCheckin && (
              <MenuItem
                onClick={e => {
                  model.handlers.checkin(e);
                  entryMenu.close();
                }}
              >
                <ListItemIcon>
                  <LocalActivityIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('participants.checkinParticipant')}</ListItemText>
              </MenuItem>
            )}
            {model.canEditRegistration && (
              <MenuItem
                onClick={e => {
                  model.handlers.editRegistration(e);
                  entryMenu.close();
                }}
              >
                <ListItemIcon>
                  <EditDocumentIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('registrations.editRegistration')}</ListItemText>
              </MenuItem>
            )}
            {model.canDeleteRegistration && (
              <MenuItem
                onClick={e => {
                  model.handlers.deleteRegistration(e);
                  entryMenu.close();
                }}
              >
                <ListItemIcon>
                  <DeleteForeverIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('registrations.deleteRegistration')}</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

export function ParticipantSerCell({ participant, callbacks }: ParticipantCellProps) {
  const { t, serialDisplay, isRegistered, registerForEditionLabel, handlers } = useParticipantRowModel(
    participant,
    callbacks,
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={e => e.stopPropagation()}>
      <span>{serialDisplay}</span>
      {callbacks.register && canRegisterParticipant(participant) && (
        <RegistrationAction
          isRegistered={isRegistered}
          onRegister={handlers.register}
          registerLabel={t('registrations.register')}
          registerTooltip={registerForEditionLabel}
          size="small"
        />
      )}
    </Box>
  );
}

export function isParticipantRowClickable(
  participant: IEnrichedParticipant,
  layout: 'admin' | 'my',
  callbacks: ParticipantCellCallbacks,
): boolean {
  const isRegistered = !!(callbacks.registrationId || participant.registration);
  return (
    layout === 'my' &&
    !!callbacks.register &&
    !isRegistered &&
    canRegisterParticipant(participant)
  );
}
